import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { 
  Typography, 
  Box, 
  Card, 
  CardHeader, 
  CardContent, 
  Grid,
  Paper,
  CircularProgress
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import WidgetsIcon from '@mui/icons-material/Widgets';
import ActionSettingsLayout from '../ActionSettingsHeader';
import { useActionSettings } from '../../hooks/useActionSettings';
import { useWebSocket } from '../WebSocketContext';
import AddWidgetModal from '../Widgets/AddWidgetModal';
import { normalizeTagVariable } from '../../Utils/tagUtils';

const ActionsGenerateWidget = ({ caseid, tags, settingId, onSaveStatusChange }) => {
  const { t } = useTranslation();
  const { ws, sendMessage, subscribeToMessages } = useWebSocket();
  const [widgets, setWidgets] = useState([]);
  const [widgetsLoading, setWidgetsLoading] = useState(false);
  const noop = useCallback(() => {}, []);

  const actionSettings = useActionSettings({
    settingId,
    caseid,
    actionType: 'generatewidget',
    onSaveStatusChange,
    customComparison: () => true, // No form data to compare
    customSaveData: () => ({
      // Preserve the tag data when saving
      tag: normalizeTagVariable(actionSettings.initialSettings?.current?.tag || '')
    }),
    loadDataFunction: () => {} // No data to load
  });

  const widgetTag = normalizeTagVariable(actionSettings.initialSettings?.current?.tag || '');

  const loadWidgets = useCallback(() => {
    if (!caseid || !sendMessage) {
      return;
    }
    setWidgetsLoading(true);
    sendMessage({
      command: 'getWidgets',
      caseid
    });
  }, [caseid, sendMessage]);

  useEffect(() => {
    if (!ws || !caseid || !widgetTag) {
      setWidgetsLoading(false);
      return;
    }
    loadWidgets();
  }, [ws, caseid, widgetTag, loadWidgets]);

  useEffect(() => {
    const unsubscribe = subscribeToMessages(['getWidgets', 'updateWidget'], (message) => {
      if (message.command === 'getWidgets') {
        const payload = message.data;
        const payloadCaseId = payload?.caseid ?? payload?.case_id ?? payload?.project_id;
        if (payloadCaseId && String(payloadCaseId) !== String(caseid)) {
          return;
        }
        if (payload?.status === 'pending') {
          setWidgetsLoading(true);
          return;
        }
        const widgetsData = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.widgets)
            ? payload.widgets
            : [];
        setWidgets(widgetsData);
        setWidgetsLoading(false);
      } else if (message.command === 'updateWidget' && message.data?.success) {
        const payloadCaseId = message.data.caseid ?? message.data.case_id ?? message.data.project_id;
        if (payloadCaseId && String(payloadCaseId) !== String(caseid)) {
          return;
        }
        const updatedWidget = message.data.widget;
        if (!updatedWidget?.id) {
          loadWidgets();
          return;
        }
        setWidgets((previousWidgets) =>
          previousWidgets.map((widget) =>
            String(widget.id) === String(updatedWidget.id) ? updatedWidget : widget
          )
        );
      }
    });
    return unsubscribe;
  }, [subscribeToMessages, loadWidgets, caseid]);

  const editingWidget = useMemo(() => {
    const normalizedTag = normalizeTagVariable(widgetTag).toLowerCase();
    if (!normalizedTag) {
      return null;
    }
    return widgets.find((widget) =>
      normalizeTagVariable(widget?.variable).toLowerCase() === normalizedTag
    ) || null;
  }, [widgetTag, widgets]);

  const handleWidgetSaved = useCallback((updatedWidget) => {
    if (!updatedWidget?.id) {
      loadWidgets();
      return;
    }
    setWidgets((previousWidgets) =>
      previousWidgets.map((widget) =>
        String(widget.id) === String(updatedWidget.id) ? updatedWidget : widget
      )
    );
  }, [loadWidgets]);

  return (
    <ActionSettingsLayout
      title="Update Widget"
      isEnabled={actionSettings.isEnabled}
      onEnabledChange={actionSettings.setIsEnabled}
      isSaveDisabled={actionSettings.isSaveDisabled}
      onSave={actionSettings.handleSave}
      onForceRun={actionSettings.handleForceRun}
      forceRunLoading={actionSettings.forceRunLoading}
      conditions={actionSettings.conditions}
      onConditionsChange={actionSettings.handleConditionsChange}
      popUpOpen={actionSettings.popUpOpen}
      forceRunResult={actionSettings.forceRunResult}
      onCloseStatusWindow={actionSettings.handleCloseStatusWindow}
      forceRunWarningOpen={actionSettings.forceRunWarningOpen}
      forceRunWarningCount={actionSettings.forceRunWarningCount}
      onForceRunConfirm={actionSettings.handleForceRunConfirm}
      onForceRunCancel={actionSettings.handleForceRunCancel}
      tags={tags}
      primaryColor="#2e7d2e"
    >
      <Grid item xs={12}>
          <Card sx={{
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
            border: '1px solid #2e7d2e10',
            overflow: 'hidden',
          }}>
            <CardHeader
              title={
                <Typography sx={{
                  fontWeight: 700,
                  color: '#444',
                  fontSize: { xs: '1rem', sm: '1.1rem' },
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 1
                }}>
                  <Box component="span" sx={{
                    bgcolor: '#2e7d2e',
                    color: 'white',
                    borderRadius: '6px',
                    px: 1.5,
                    py: 0.7,
                    mr: { xs: 1, sm: 2 },
                    fontSize: '0.85rem',
                    letterSpacing: '0.5px',
                    fontWeight: 600,
                    boxShadow: '0 3px 6px rgba(46, 125, 46, 0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5
                  }}>
                    <WidgetsIcon sx={{ fontSize: '1rem' }} />
                    WIDGET
                  </Box>
                  {t('generate_widget.tag_label', 'Widget Configuration')}
                </Typography>
              }
              sx={{
                borderBottom: '1px solid #f0f0f8',
                backgroundColor: '#fafbff',
                borderTopLeftRadius: '12px',
                borderTopRightRadius: '12px',
                p: { xs: 1.5, sm: 2 },
              }}
            />
            <CardContent sx={{ p: { xs: 1.5, sm: 3 } }}>
              {widgetTag ? (
                widgetsLoading && !editingWidget ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, py: 4 }}>
                    <CircularProgress size={22} sx={{ color: '#2e7d2e' }} />
                    <Typography variant="body2" color="text.secondary">
                      {t('generate_widget.loading_widget_settings', 'Loading widget settings...')}
                    </Typography>
                  </Box>
                ) : editingWidget ? (
                  <AddWidgetModal
                    inline
                    open
                    caseid={caseid}
                    editingWidget={editingWidget}
                    onClose={noop}
                    showDeleteAction={false}
                    onWidgetSaved={handleWidgetSaved}
                  />
                ) : (
                  <Paper 
                    elevation={0}
                    sx={{ 
                      p: 4,
                      borderRadius: '12px',
                      backgroundColor: '#f9f9f9',
                      border: '2px dashed #ddd',
                      textAlign: 'center',
                      minHeight: '120px',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <WidgetsIcon sx={{ 
                      fontSize: '3rem', 
                      color: '#ccc',
                      mb: 2 
                    }} />
                    <Typography variant="body1" sx={{ 
                      color: 'text.secondary',
                      fontWeight: 500,
                      mb: 1
                    }}>
                      {t('generate_widget.widget_not_found_title', 'Widget not found')}
                    </Typography>
                    <Typography variant="body2" sx={{ 
                      color: 'text.secondary',
                      opacity: 0.8
                    }}>
                      {t('generate_widget.widget_not_found_description', 'This action points to a widget tag that is not available in the current project.')}
                    </Typography>
                  </Paper>
                )
              ) : (
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    borderRadius: '12px',
                    backgroundColor: '#f9f9f9',
                    border: '2px dashed #ddd',
                    textAlign: 'center',
                    minHeight: '120px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <WidgetsIcon sx={{
                    fontSize: '3rem',
                    color: '#ccc',
                    mb: 2
                  }} />
                  <Typography variant="body1" sx={{
                    color: 'text.secondary',
                    fontWeight: 500,
                    mb: 1
                  }}>
                    {t('generate_widget.no_tag_title', 'No Tag Configured')}
                  </Typography>
                  <Typography variant="body2" sx={{
                    color: 'text.secondary',
                    opacity: 0.8
                  }}>
                    {t('generate_widget.no_tag_description', 'A tag will be automatically assigned when this action runs.')}
                  </Typography>
                </Paper>
              )}
            </CardContent>
          </Card>
      </Grid>
    </ActionSettingsLayout>
  );
};

export default React.memo(ActionsGenerateWidget);
